import { PanelRightCloseIcon } from 'icons/PanelRightCloseIcon';
import { PanelRightOpenIcon } from 'icons/PanelRightOpenIcon';

import styles from '../DisplaySidebar.module.css';

import { useEffect, useRef, useState } from 'react';
import { forwardRef } from 'react';
import { COLOR_JAUGE, PIXLINK_CAMPAIGN_STATUS, PIXLINK_STATUS } from 'utils/Constants';
import { MyCustomHighcharts } from 'components/shared/Charts/MyCustomHighcharts';
import { calculateClientMinimumLinks, calculateMinimumLinks } from 'utils/netlinking/linksNumber';
import { calculateRefusedClient, calculateRefusedPixalione, calculateToBeCommanded, calculateToBeFilteredConsultant, calculateValidateClient, calculateValidateSuperAdmin, calculateValidateSuperConsultant } from 'utils/Jauge/Calculate';
import { MyCustomProgressBarSmall } from 'components/shared/Charts/MyCustomProgressBarSmall';
import { calculateClientRefusedClient, calculateClientToBeCommandedClient, calculateClientToBeFilteredClient, calculateClientValidateClient } from 'utils/Jauge/CalculateClient';
import { MyCustomReverseHighChats } from 'components/shared/Charts/MyCustomReverseHighChats';
import { MyCustomBiggerReverseHighChats } from 'components/shared/Charts/MyCustomBiggerReverseHighChats';
import axiosPixLink from 'config/axiosPixLink';

function SourcingFeedbacksBloc({ campaignId, t }) {
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [openDomainsClient, setOpenDomainsClient] = useState({});
    const [openDomainsSuperConsultant, setOpenDomainsSuperConsultant] = useState({});

    useEffect(() => {
        loadFeedbacks();
    }, [campaignId]);
    useEffect(() => {
        const handler = (e) => {
            if (!e.detail || e.detail.campaignId == campaignId) {
                loadFeedbacks();
            }
        };
        window.addEventListener('sourcingFeedbackUpdated', handler);
        return () => window.removeEventListener('sourcingFeedbackUpdated', handler);
    }, [campaignId]);

    const loadFeedbacks = () => {
        setLoading(true);
        axiosPixLink
            .get(`/campaign/${campaignId}/sourcing-feedbacks`, {
                params: { _t: Date.now() },       // cache-busting query param
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                },
            })
            .then((res) => {
                const raw = Array.isArray(res.data) ? res.data : [];
                setData(raw);
                const defaultOpen = {};
                raw.forEach((s) => { defaultOpen[s.netlinkingSelectedId] = false; });
                setOpenDomainsClient({ ...defaultOpen });
                setOpenDomainsSuperConsultant({ ...defaultOpen });
                setLoaded(true);
                setLoading(false);
            })
            .catch(() => { setData([]); setLoaded(true); setLoading(false); });
    };

    const renderBox = (title, role, openDomains, setOpenDomains) => {
        const normalize = (r) => (r ?? '').replace(/^ROLE_/, '').toUpperCase();

        const filtered = data
            .map((spot) => ({
                ...spot,
                feedbacks: (spot.feedbacks ?? []).filter(
                    (fb) => normalize(fb.authorRole) === normalize(role)
                ),
            }))
            .filter((spot) => spot.feedbacks.length > 0);

        const toggleDomain = (id) => {
            setOpenDomains((prev) => ({ ...prev, [id]: !prev[id] }));
        };

        return (
            <div className="box-wrapper" style={{ marginTop: 16 }}>
                <div className="header-title mb-0">{t(title)}</div>
                <div style={{ marginTop: 20 }}>
                    {!loading && loaded && filtered.length === 0 && (
                        <div className="sub-header-title">{t('Aucun commentaire pour cette campagne')}</div>
                    )}
                    {!loading && filtered.map((spot, spotIndex) => (
                        <div key={spot.netlinkingSelectedId} style={{ marginTop: spotIndex === 0 ? 0 : 8 }}>
                            <div
                                onClick={() => toggleDomain(spot.netlinkingSelectedId)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    backgroundColor: '#E0E0E0',
                                    padding: '7px 12px',
                                    cursor: 'pointer',
                                    borderRadius: 6,
                                }}
                            >
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontSize: 15 }}>
                                    <span style={{ marginRight: 6 }}>
                                        {openDomains[spot.netlinkingSelectedId] ? '▾' : '▸'}
                                    </span>
                                    {spot.domain}
                                </span>
                                <span style={{ fontWeight: 'normal', fontSize: 12, color: '#666', marginLeft: 8, whiteSpace: 'nowrap' }}>
                                    ({spot.feedbacks.length})
                                </span>
                            </div>
                            {openDomains[spot.netlinkingSelectedId] && spot.feedbacks.map((fb, i) => (
                                <div
                                    key={fb.id}
                                    style={{
                                        padding: '7px 12px 7px 20px',
                                        borderBottom: i < spot.feedbacks.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    }}
                                >
                                    <div style={{ fontSize: 15, color: '#444' }}>{fb.comment}</div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            {renderBox('Commentaires client', 'ROLE_CLIENT', openDomainsClient, setOpenDomainsClient)}
        </>
    );
}

// ─── ClientDisplaySidebar ─────────────────────────────────────────────────────

export const ClientDisplaySidebar = forwardRef(
    (
        {
            collapsed,
            setCollapsed,
            width = 400,
            minWidth = 400,
            maxWidth = 500,
            setWidth,
            t,
            netlinkings,
            netlinkingsTmp,
            campaignStatus,
            finished,
            campaignBudget,
            campaignTarif,
            campaignId,         // ← nouveau
        },
        ref
    ) => {
        const isResizing = useRef(false);

        const handleMouseDown = () => {
            isResizing.current = true;
            document.body.classList.add('resizing');
        };

        const handleMouseMove = (event) => {
            if (!isResizing.current || collapsed) return;
            document.body.classList.add('resizing');
            const calculatedWidth = window.innerWidth - event.clientX;
            const newWidth = Math.min(maxWidth, Math.max(minWidth, calculatedWidth));
            setWidth(newWidth);
        };

        const handleMouseUp = () => {
            isResizing.current = false;
            document.body.classList.remove('resizing');
        };

        useEffect(() => {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }, []);

        return (
            <>
                <button
                    className={styles.sidebarToggleButton}
                    style={{ right: collapsed ? 5 : width + 5 }}
                    onClick={() => setCollapsed(!collapsed)}
                >
                    {collapsed ? (
                        <PanelRightOpenIcon width={28} height={28} />
                    ) : (
                        <PanelRightCloseIcon width={28} height={28} />
                    )}
                </button>

                <div className={styles.sidebar} style={{ width: collapsed ? 0 : width }}>
                    <div onMouseDown={handleMouseDown} className={styles.resizeHandler} />
                    <div ref={ref} className={styles.sidebarContent}>

                        <div></div>

                        {(netlinkings.filter(
                            (item) =>
                                item.spotStatus === PIXLINK_STATUS.TO_BE_VALIDATED_CLIENT ||
                                item.spotStatus === PIXLINK_STATUS.TO_BE_FILTERED_CONSULTANT ||
                                item.spotStatus === PIXLINK_STATUS.TO_BE_COMMANDED ||
                                item.spotStatus === PIXLINK_STATUS.REFUSED_CLIENT
                        ).length > 0 && !finished) && (
                                <div>
                                    <div className="box-wrapper">
                                        <div className="header-title mb-0">
                                            {t('Nombre de liens par Status')}
                                        </div>
                                        <div className="box-graph">
                                            <div className="d-flex justify-content-center">
                                                <span>
                                                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap' }}>
                                                        {calculateClientValidateClient(netlinkings) > 0 && (
                                                            <MyCustomProgressBarSmall defaultColor={COLOR_JAUGE.ORANGE} label={t('A valider client')} value={calculateClientValidateClient(netlinkings)} />
                                                        )}
                                                        {calculateClientRefusedClient(netlinkings) > 0 && (
                                                            <MyCustomProgressBarSmall label={t('Refusé client')} defaultColor={COLOR_JAUGE.RED} value={calculateClientRefusedClient(netlinkings)} />
                                                        )}
                                                        {calculateClientToBeFilteredClient(netlinkings) > 0 && (
                                                            <MyCustomProgressBarSmall defaultColor={COLOR_JAUGE.BLUE} label={t('Validé client')} value={calculateClientToBeFilteredClient(netlinkings)} />
                                                        )}
                                                        {calculateClientToBeCommandedClient(netlinkings) > 0 && (
                                                            <MyCustomProgressBarSmall label={t('A commander')} defaultColor={COLOR_JAUGE.GREEN} value={calculateClientToBeCommandedClient(netlinkings)} />
                                                        )}
                                                    </div>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        <div className="box-wrapper">
                            <div className="header-title mb-0">{t('Reste à consommer')}</div>
                            <div className="box-graph">
                                <div className="d-flex justify-content-center">
                                    <span>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <MyCustomBiggerReverseHighChats
                                                defaultColor="#FF8D4B"
                                                value={campaignTarif}
                                                maxValue={campaignBudget}
                                            />
                                        </div>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* ── Commentaires client ── */}
                        <SourcingFeedbacksBloc campaignId={campaignId} t={t} />

                    </div>
                </div>
            </>
        );
    }
);

export default ClientDisplaySidebar;
